// src/pages/DeleteAccount.tsx
import React, { useEffect, useState } from 'react';
import Footer from '../../components/Footer';
import {
  Emphasis,
  InnerCard,
  InnerContainer,
  InnerSection,
  OrderedItem,
  OrderedList,
  PageTitle,
  Paragraph,
  SectionTitle,
  TextLink,
} from '../../components/InnerPageLayout';
import JsonData from '../../data/data.json';
import { SiteData } from '../../types/siteDataTypes';
import InnerPageHeader from '../../components/InnerPageHeader';

const APP_NAME = 'Chot Study';
const DEVELOPER_NAME = 'Chots AI';

const DeleteAccount: React.FC = () => {
  const [siteData, setSiteData] = useState<SiteData | null>(null);

  useEffect(() => {
    setSiteData(JsonData as SiteData);
  }, []);

  return (
    <>
    {siteData ? (
      <>
          <InnerPageHeader data={siteData.ChotStudyPrivacyHeader} />
          <InnerSection>
            <InnerContainer>
              <InnerCard>
                <PageTitle>Delete Your {APP_NAME} Account</PageTitle>
                <Paragraph>
                  At {DEVELOPER_NAME}, we respect your decision if you choose to delete your
                  account. Below are the steps to delete your {APP_NAME} account and information
                  about how we handle your data upon deletion.
                </Paragraph>

                <SectionTitle>Steps to Delete Your Account</SectionTitle>
                <OrderedList>
                  <OrderedItem>
                    <Emphasis>Open the App:</Emphasis> Launch the {APP_NAME} app on your device.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Access the Side Menu:</Emphasis> Tap on the menu icon to open the
                    side menu.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Navigate to User Preferences:</Emphasis> Select{' '}
                    <i>"User Preferences"</i> from the menu options.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Initiate Account Deletion:</Emphasis> Scroll down and tap on{' '}
                    <i>"Delete Account"</i>.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Confirm Deletion:</Emphasis> Follow the on-screen instructions to
                    confirm the deletion of your account.
                  </OrderedItem>
                </OrderedList>

                <SectionTitle>Data Deletion and Retention Policy</SectionTitle>
                <Paragraph>When you delete your {APP_NAME} account:</Paragraph>
                <OrderedList>
                  <OrderedItem>
                    <Emphasis>Personal Information:</Emphasis> Your personal information, including
                    name, email address, and personal details, will be permanently deleted from our
                    active databases.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Chat Messages:</Emphasis> All conversations and chat messages with AI
                    assistants associated with your account will be deleted within 30 days of
                    account deletion.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Backup Archives:</Emphasis> Some data may remain in backup archives for
                    up to 90 days but will not be used for any purpose other than compliance with
                    legal obligations.
                  </OrderedItem>
                  <OrderedItem>
                    <Emphasis>Aggregated Data:</Emphasis> We may retain aggregated, non-personally
                    identifiable data for analytical purposes.
                  </OrderedItem>
                </OrderedList>

                <SectionTitle>Need Assistance?</SectionTitle>
                <Paragraph>
                  If you encounter any issues during the account deletion process or have any
                  questions, please contact our support team at{' '}
                  <TextLink
                    href={`mailto:${siteData.AppsData.customerSupportEmail}?subject=Regarding ${APP_NAME}'s Account Deletion`}
                  >
                    {siteData.AppsData.customerSupportEmail}
                  </TextLink>
                  .
                </Paragraph>
              </InnerCard>
            </InnerContainer>
          </InnerSection>
          <Footer />
        </>
      ) : (
        <p>Loading...</p>
      )}
      </>
  );
};

export default DeleteAccount;
